import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ManagersState } from "../../NGRX/manager/manager.model";
import { RouterStateUrl } from "../core.state";
import { selectConfiguration } from "../configuration/configuration.selectors";
import { selectRouter } from "../router/router.selector";
import { ConfigurationScope, ConfigurationState } from '../configuration/configuration.models';
import { RouterReducerState } from '@ngrx/router-store';

// Feature selector
export const selectManagersState = createFeatureSelector<ManagersState>('managers');

// Basic state selectors with memoization
export const selectManagersList = createSelector(
    selectManagersState,
    (state: ManagersState) => state.list
);

export const selectManager = createSelector(
    selectManagersState,
    (state: ManagersState) => state.manager
);

export const selectMyManager = createSelector(
    selectManagersState,
    (state: ManagersState) => state.my_manager
);

export const selectManagerUpdateError = createSelector(
    selectManagersState,
    (state: ManagersState) => state.update_error
);

// Helper functions for complex logic
const isCurrentUserManager = (myManager: any, routerManagerId: any): boolean => {
    if (!myManager) return false;
    return String(myManager.id) === String(routerManagerId);
};

// Complex selectors with helper functions
export const selectCurrentManager = createSelector(
    selectManagersState,
    selectRouter,
    (state: ManagersState, routerState: RouterReducerState<RouterStateUrl>) => {
        const routerManagerId = routerState?.state.params['manager_id'];
        return isCurrentUserManager(state.my_manager, routerManagerId)
            ? state.my_manager
            : state.manager;
    }
);

export const selectManagersFilters = createSelector(
    selectManagersState,
    (state: ManagersState) => state.listParams
);

export const selectLeaderboard = createSelector(
    selectManagersState,
    (state: ManagersState) => state.leaderboard
);

export const selectTopFollowed = createSelector(
    selectManagersState,
    (state: ManagersState) => state.top_followed
);

// Helper function for rebate calculation
const calculateRebateAmount = (weeklyRebateUnits: string | number | undefined, fxRate: string | number | undefined): number => {
    if (!weeklyRebateUnits || !fxRate) return 0;
    return Number(weeklyRebateUnits) * Number(fxRate);
};

export const selectRebate = createSelector(
    selectManagersState,
    selectConfiguration,
    (state: ManagersState, configuration: ConfigurationState): number => {
        return calculateRebateAmount(
            state.my_manager?.live_rebate_rate?.weekly_rebate_units,
            configuration.fxRates?.rate
        );
    }
);

// Helper function for finding current rebate tier
const findRebateTier = (
    tiers: ConfigurationScope.RebateTier[] | undefined,
    tierId: string | number | undefined
): ConfigurationScope.RebateTier | undefined => {
    if (!tiers || tierId === undefined) return undefined;
    const numericTierId = typeof tierId === 'string' ? parseInt(tierId, 10) : tierId;
    return tiers.find(tier => tier.id === numericTierId);
};

export const selectCurrentRebate = createSelector(
    selectManagersState,
    selectConfiguration,
    (state: ManagersState, configuration: ConfigurationState): ConfigurationScope.RebateTier | undefined => {
        return findRebateTier(
            configuration.coreSettings?.rebate_tiers,
            state.my_manager?.live_rebate_rate?.rebate_tier_id
        );
    }
);

// Memoized array selector with default empty array
export const selectPinnedSyndicates = createSelector(
    selectManagersState,
    (state: ManagersState) => state.my_manager?.pinnedSyndicates ?? []
);

export const selectTopRoiManagers = createSelector(
    selectManagersState,
    (state: ManagersState) => state.top_positive_roi
);

